import { useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';
import {
  COOKIE_CONSENT_ACCEPTED,
  COOKIE_CONSENT_DECLINED,
  COOKIE_CONSENT_NAME,
  readCookieConsent,
  setCookieConsent,
} from '@/lib/cookie-consent';
import { cn } from '@/lib/utils';

/**
 * Barra inferior de consentimento — compacta, alinhada ao visual do portal.
 * A lógica de cookie/consentimento permanece em `@/lib/cookie-consent`.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (readCookieConsent() !== null) return;
    setVisible(true);
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setEntered(true);
      return;
    }
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setCookieConsent(COOKIE_CONSENT_ACCEPTED);
    setVisible(false);
  };

  const decline = () => {
    setCookieConsent(COOKIE_CONSENT_DECLINED);
    setVisible(false);
  };

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-[220]',
        'pointer-events-none',
        'px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 sm:px-5 sm:pb-[max(1rem,env(safe-area-inset-bottom))]',
        'transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
        entered ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
      )}
      aria-live="polite"
    >
      <div
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-desc"
        className={cn(
          'pointer-events-auto relative mx-auto w-full max-w-7xl',
          'overflow-hidden rounded-2xl',
          'border border-white/[0.12] backdrop-blur-md',
          'shadow-[0_-4px_32px_-8px_rgba(0,11,106,0.45),0_0_0_1px_rgba(56,189,248,0.06)]'
        )}
        style={{
          background:
            'linear-gradient(90deg, rgba(3,0,35,0.94) 0%, rgba(7,18,56,0.95) 50%, rgba(0,11,106,0.92) 100%)',
        }}
      >
        {/* Hairline superior — mesmo idioma visual do footer / tooltips da Equipe */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/35 to-transparent"
          aria-hidden
        />

        <div
          className={cn(
            'relative flex flex-col gap-3.5 px-4 py-3.5',
            'sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-5 sm:py-3.5'
          )}
        >
          {/* Conteúdo */}
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
            <span
              className={cn(
                'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                'bg-white/[0.08] ring-1 ring-sky-400/25',
                'sm:mt-0'
              )}
              aria-hidden
            >
              <Cookie className="h-4 w-4 text-sky-200" strokeWidth={2} />
            </span>

            <div className="min-w-0 text-left">
              <p
                id="cookie-consent-title"
                className="font-poppins text-sm font-semibold tracking-tight text-white"
              >
                Cookies
              </p>
              <p
                id="cookie-consent-desc"
                className="mt-0.5 font-inter text-[12.5px] leading-snug text-blue-100 sm:text-[13px] sm:leading-relaxed"
              >
                Usamos cookies para melhorar sua experiência e aprimorar nosso portal. Você pode
                aceitar ou recusar cookies não essenciais. Sua escolha pode ser alterada a qualquer
                momento.
              </p>
            </div>
          </div>

          {/* Ações */}
          <div
            className={cn(
              'flex w-full shrink-0 items-stretch gap-2',
              'sm:w-auto sm:items-center'
            )}
          >
            <button
              type="button"
              onClick={decline}
              className={cn(
                'inline-flex h-10 flex-1 items-center justify-center rounded-xl px-4',
                'border border-white/20 bg-white/[0.05]',
                'font-poppins text-[13px] font-semibold text-blue-50',
                'transition-[background-color,border-color,color,transform] duration-150 ease-out',
                'hover:border-white/35 hover:bg-white/[0.1] hover:text-white',
                'active:scale-[0.98]',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300/70',
                'sm:flex-none sm:min-w-[7.25rem]',
                'motion-reduce:active:scale-100'
              )}
            >
              Recusar
            </button>
            <button
              type="button"
              onClick={accept}
              className={cn(
                'inline-flex h-10 flex-1 items-center justify-center rounded-xl px-4',
                'bg-white text-[#000b6a]',
                'font-poppins text-[13px] font-bold',
                'shadow-[0_6px_18px_-6px_rgba(255,255,255,0.35)]',
                'transition-[background-color,box-shadow,transform] duration-150 ease-out',
                'hover:bg-sky-50 hover:shadow-[0_8px_22px_-6px_rgba(255,255,255,0.45)]',
                'active:scale-[0.98]',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                'sm:flex-none sm:min-w-[7.25rem]',
                'motion-reduce:active:scale-100'
              )}
            >
              Aceitar
            </button>
          </div>
        </div>

        <span className="sr-only" data-cookie-name={COOKIE_CONSENT_NAME} />
      </div>
    </div>
  );
}
